import { useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import {
  Button,
  Card,
  Col,
  Descriptions,
  Popconfirm,
  Result,
  Row,
  Skeleton,
  Space,
  Switch,
  Typography,
  App as AntApp,
} from 'antd';
import {
  ArrowLeftOutlined,
  CheckCircleOutlined,
  CopyOutlined,
  DeleteOutlined,
  QrcodeOutlined,
  SendOutlined,
} from '@ant-design/icons';
import { QRCodeSVG } from 'qrcode.react';
import { useTranslation } from 'react-i18next';

import {
  useCustomerQuery,
  useDeleteCustomerMutation,
  useIssueLinkTokenMutation,
  useUpdateCustomerMutation,
  type LinkToken,
} from './hooks';
import { SendMessageDialog } from '@/components/SendMessageDialog';
import { ErrorAlert } from '@/components/ErrorAlert';
import { ApiError } from '@starling/core/api/errors';

const { Title, Text, Paragraph } = Typography;

function formatDate(value: string | null): string {
  if (!value) return '—';
  try {
    return new Date(value).toLocaleString('ru-RU');
  } catch {
    return value;
  }
}

export function CustomerDetailPage(): JSX.Element {
  const { t } = useTranslation();
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { message } = AntApp.useApp();

  const customerQuery = useCustomerQuery(id);
  const issueLinkTokenMutation = useIssueLinkTokenMutation();
  const updateMutation = useUpdateCustomerMutation();
  const deleteMutation = useDeleteCustomerMutation();

  const [linkToken, setLinkToken] = useState<LinkToken | null>(null);
  const [sendDialogOpen, setSendDialogOpen] = useState(false);

  if (customerQuery.isLoading) {
    return (
      <Card>
        <Skeleton active />
      </Card>
    );
  }

  if (customerQuery.isError) {
    const err = customerQuery.error;
    if (err instanceof ApiError && err.status === 404) {
      return (
        <Result
          status="404"
          title={t('customer.not_found')}
          extra={
            <Button type="primary" onClick={() => navigate('/customers')}>
              {t('customer.back_to_list')}
            </Button>
          }
        />
      );
    }
    return <ErrorAlert error={err} />;
  }

  const c = customerQuery.data;
  if (!c || !id) return <ErrorAlert error={new Error(t('customer.not_found'))} />;

  const isLinked = c.linked_at !== null;

  const handleShowQr = (): void => {
    if (isLinked) {
      void message.info(t('customer.qr.already_linked'));
      return;
    }
    issueLinkTokenMutation.mutate(id, {
      onSuccess: (token) => setLinkToken(token),
      onError: (err) => {
        if (err instanceof ApiError && err.code === 'customer_already_linked') {
          void message.info(t('customer.qr.already_linked'));
          void customerQuery.refetch();
        } else {
          void message.error(err instanceof Error ? err.message : t('errors.generic'));
        }
      },
    });
  };

  const handleCopyDeepLink = async (): Promise<void> => {
    if (!linkToken) return;
    try {
      await navigator.clipboard.writeText(linkToken.deep_link_url);
      void message.success(t('common.copied'));
    } catch {
      void message.error(t('errors.generic'));
    }
  };

  const handleConsentToggle = (checked: boolean): void => {
    if (!id) return;
    updateMutation.mutate(
      { id, patch: { consent_marketing: checked } },
      {
        onSuccess: () => {
          void message.success(t('customer.consent_marketing_updated'));
        },
        onError: (err) => {
          void message.error(err instanceof Error ? err.message : t('errors.generic'));
        },
      },
    );
  };

  const handleDelete = (): void => {
    if (!id) return;
    deleteMutation.mutate(id, {
      onSuccess: () => {
        void message.success(t('customer.deleted_success'));
        navigate('/customers');
      },
      onError: (err) => {
        // 404 — клиент уже удалён в параллельной вкладке; всё равно уводим
        // на список и показываем мягкое сообщение, а не ошибку.
        if (err instanceof ApiError && err.status === 404) {
          void message.info(t('customer.deleted_already'));
          navigate('/customers');
          return;
        }
        void message.error(err instanceof Error ? err.message : t('errors.generic'));
      },
    });
  };

  return (
    <div>
      <Link
        to="/customers"
        style={{ display: 'inline-flex', alignItems: 'center', gap: 6, marginBottom: 16 }}
      >
        <ArrowLeftOutlined /> {t('customer.back_to_list')}
      </Link>

      <Row gutter={[24, 24]}>
        <Col xs={24} md={12}>
          <Card title={c.name}>
            <Descriptions column={1} size="small">
              <Descriptions.Item label={t('customers.table.phone')}>
                <span className="sl-mono">{c.phone}</span>
              </Descriptions.Item>
              <Descriptions.Item label={t('customer.consent_marketing')}>
                <Switch
                  checked={c.consent_marketing}
                  loading={updateMutation.isPending}
                  onChange={handleConsentToggle}
                  checkedChildren={t('common.yes')}
                  unCheckedChildren={t('common.no')}
                />
              </Descriptions.Item>
              <Descriptions.Item label={t('customer.linked_at')}>
                <span className="sl-mono">{formatDate(c.linked_at)}</span>
              </Descriptions.Item>
              <Descriptions.Item label={t('customer.telegram_chat_id')}>
                <span className="sl-mono">{c.telegram_chat_id ?? '—'}</span>
              </Descriptions.Item>
              <Descriptions.Item label={t('customer.created_at')}>
                <span className="sl-mono">{formatDate(c.created_at)}</span>
              </Descriptions.Item>
            </Descriptions>

            <div
              style={{
                marginTop: 24,
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
                gap: 12,
                flexWrap: 'wrap',
              }}
            >
              {/* M3.3+: Telegram-привязка больше не обязательна для отправки.
                  Backend dispatch выберет SMS-fallback при SMS_ENABLED=true.
                  Если SMS отключён, бэк отдаст 422, ошибка покажется в SendDialog. */}
              <Button
                type="primary"
                icon={<SendOutlined />}
                size="large"
                onClick={() => setSendDialogOpen(true)}
              >
                {t('customer.send_test')}
              </Button>

              <Popconfirm
                title={t('customer.delete_confirm_title')}
                description={t('customer.delete_confirm_description')}
                okText={t('customer.delete_confirm_ok')}
                cancelText={t('common.cancel')}
                okButtonProps={{ danger: true, loading: deleteMutation.isPending }}
                onConfirm={handleDelete}
              >
                <Button danger icon={<DeleteOutlined />} loading={deleteMutation.isPending}>
                  {t('customer.delete_button')}
                </Button>
              </Popconfirm>
            </div>
          </Card>
        </Col>

        <Col xs={24} md={12}>
          {isLinked ? (
            <Card
              style={{
                background: 'var(--health-turquoise-200)',
                borderColor: 'var(--health-turquoise-600)',
              }}
            >
              <Space align="start" size="middle">
                <CheckCircleOutlined
                  style={{ fontSize: 28, color: 'var(--health-turquoise-600)' }}
                />
                <div>
                  <Title level={5} style={{ color: 'var(--health-turquoise-600)', marginTop: 0 }}>
                    {t('customer.linked_label')}
                  </Title>
                  <Text>{t('customer.linked_subtitle')}</Text>
                </div>
              </Space>
            </Card>
          ) : (
            <Card title={t('customer.qr.title')}>
              <Paragraph type="secondary">{t('customer.qr.subtitle')}</Paragraph>

              {!linkToken && !issueLinkTokenMutation.isPending && (
                <Button
                  type="primary"
                  size="large"
                  icon={<QrcodeOutlined />}
                  onClick={handleShowQr}
                >
                  {t('customer.show_qr')}
                </Button>
              )}

              {issueLinkTokenMutation.isPending && <Skeleton.Image active />}

              {issueLinkTokenMutation.error && !linkToken && (
                <ErrorAlert error={issueLinkTokenMutation.error} />
              )}

              {linkToken && (
                <Space direction="vertical" size="large" style={{ width: '100%' }}>
                  <div
                    style={{
                      display: 'flex',
                      justifyContent: 'center',
                      padding: 16,
                      background: '#fff',
                      borderRadius: 12,
                    }}
                  >
                    <QRCodeSVG value={linkToken.deep_link_url} size={256} level="M" includeMargin />
                  </div>
                  <div>
                    <Text
                      type="secondary"
                      style={{ fontSize: 12, display: 'block', marginBottom: 4 }}
                    >
                      {t('customer.qr.deep_link_label')}
                    </Text>
                    <div className="sl-deep-link">{linkToken.deep_link_url}</div>
                  </div>
                  <Space>
                    <Button icon={<CopyOutlined />} onClick={handleCopyDeepLink}>
                      {t('customer.qr.copy_link')}
                    </Button>
                    <Text type="secondary" style={{ fontSize: 12 }}>
                      {t('customer.qr.expires_at')}: {formatDate(linkToken.expires_at)}
                    </Text>
                  </Space>
                </Space>
              )}
            </Card>
          )}
        </Col>
      </Row>

      <SendMessageDialog
        open={sendDialogOpen}
        customerId={id}
        onClose={() => setSendDialogOpen(false)}
      />
    </div>
  );
}
